export function filterCustomers(rows, search) {
  const q = search.trim().toLowerCase();
  if (!q) return rows;
  return rows.filter((r) => {
    const name = String(r?.name ?? "").toLowerCase();
    const email = String(r?.email ?? "").toLowerCase();
    const location = String(r?.location ?? "").toLowerCase();
    return name.includes(q) || email.includes(q) || location.includes(q);
  });
}

export function paginateSlice(items, page, pageSize) {
  const safePage = Math.max(1, page);
  const start = (safePage - 1) * pageSize;
  return items.slice(start, start + pageSize);
}

export function totalPagesFor(itemsLength, pageSize) {
  return Math.max(1, Math.ceil(itemsLength / pageSize));
}

function formatSellerCustomerMoney(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return "$0.00";
  return `$${n.toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

function formatSellerCustomerDate(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  const day = d.getDate();
  const month = d.toLocaleDateString("en-US", { month: "long" });
  const year = d.getFullYear();
  const ordinal = (() => {
    if (day >= 11 && day <= 13) return "th";
    switch (day % 10) {
      case 1:
        return "st";
      case 2:
        return "nd";
      case 3:
        return "rd";
      default:
        return "th";
    }
  })();
  return `${day}${ordinal} ${month}, ${year}`;
}

/**
 * Map a `getSellerCustomers` API doc to the row shape consumed by
 * `CustomersTable` / `CustomersRow`.
 */
export function mapSellerCustomerToRow(doc) {
  if (!doc) return null;
  const id = doc.customerId != null ? String(doc.customerId) : "";
  return {
    id,
    name: String(doc.customerName ?? "—"),
    email: "",
    location: "",
    totalOrders: Number(doc.totalOrders) || 0,
    totalSpent: formatSellerCustomerMoney(doc.totalSpent),
    lastOrderLabel: formatSellerCustomerDate(doc.lastOrderDate),
    raw: doc,
  };
}

function pickOrderItemAmount(item) {
  if (item?.amountPaid != null) return Number(item.amountPaid);
  if (item?.sellerAmount != null) return Number(item.sellerAmount);
  const unit = Number(item?.pricePerUnit);
  const qty = Number(item?.quantity);
  if (Number.isFinite(unit) && Number.isFinite(qty)) return unit * qty;
  return 0;
}

function normalizeCustomerOrderUiStatus(apiStatus) {
  const raw = String(apiStatus ?? "").trim();
  if (!raw) return "processing";
  const u = raw.toUpperCase().replace(/\s+/g, "_");
  if (u.includes("CANCEL")) return "cancelled";
  if (u.includes("FAIL")) return "failed_delivery";
  if (u === "DELIVERED" || (u.includes("DELIVER") && !u.includes("OUT")))
    return "delivered";
  if (u.includes("OUT") && u.includes("DELIV")) return "out_delivery";
  if (u.includes("PICKUP") || u.includes("PICK_UP") || u.includes("READY"))
    return "ready_pickup";
  if (
    u.includes("NEW") ||
    u.includes("PLACED") ||
    u.includes("PENDING") ||
    u.includes("CREATED") ||
    u === "PAID" ||
    u.includes("PAYMENT_CONFIRMED")
  )
    return "new_order";
  if (u.includes("PROCESS")) return "processing";
  return "processing";
}

/**
 * Build the drawer viewmodel from the customer row + the `getCustomerOrderHistory`
 * items.
 */
export function buildCustomerDetailFromOrders(customer, items) {
  if (!customer) return null;
  const safeItems = Array.isArray(items) ? items.filter(Boolean) : [];

  const sortedAsc = [...safeItems].sort((a, b) => {
    const aTs = a?.createdAt ? new Date(a.createdAt).getTime() : 0;
    const bTs = b?.createdAt ? new Date(b.createdAt).getTime() : 0;
    return aTs - bTs;
  });

  const firstOrder = sortedAsc[0];
  const lastOrder = sortedAsc[sortedAsc.length - 1];

  const totalSpentValue = safeItems.reduce(
    (acc, item) => acc + pickOrderItemAmount(item),
    0,
  );

  const orderHistory = [...safeItems]
    .sort((a, b) => {
      const aTs = a?.createdAt ? new Date(a.createdAt).getTime() : 0;
      const bTs = b?.createdAt ? new Date(b.createdAt).getTime() : 0;
      return bTs - aTs;
    })
    .map((item) => {
      const id = item?._id != null ? String(item._id) : "";
      const ref = item?.reference != null ? String(item.reference) : id;
      return {
        orderRouteId: id,
        orderRef: ref ? `#ORD-${ref}` : "—",
        price: formatSellerCustomerMoney(pickOrderItemAmount(item)),
        status: normalizeCustomerOrderUiStatus(item?.status),
        createdAt: item?.createdAt ?? null,
      };
    });

  return {
    ...customer,
    detailLocation:
      customer.location && customer.location.trim() ? customer.location : "—",
    firstOrderLabel: firstOrder?.createdAt
      ? formatSellerCustomerDate(firstOrder.createdAt)
      : "—",
    lastOrderLabel: lastOrder?.createdAt
      ? formatSellerCustomerDate(lastOrder.createdAt)
      : customer.lastOrderLabel ?? "—",
    totalSpentHighlight:
      safeItems.length > 0
        ? formatSellerCustomerMoney(totalSpentValue)
        : customer.totalSpent ?? formatSellerCustomerMoney(0),
    orderHistory,
  };
}
